import type {
  CropArea,
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import type { Translate } from '@shared/hooks/locale';

const PROCESSED_IMAGE_CACHE = new Map<string, File>();
const MAX_CACHE_SIZE = 10;

const getCacheKey = (
  source: ImageSource,
  transform: ImageTransform,
): string => {
  const sourceKey =
    source.type === 'file'
      ? `file-${(source.data as File).name}-${
          (source.data as File).lastModified
        }`
      : `url-${source.data}`;

  const transformKey = JSON.stringify(transform);
  return `${sourceKey}-${transformKey}`;
};

const getFileFromSource = async (
  source: ImageSource,
  t: (key: string) => string,
): Promise<File> => {
  // Case 1: Already a file - just validate and return
  if (source.type === 'file') {
    const file = source.data as File;
    return file;
  }

  // Case 2: Image URL - fetch and convert to File
  if (source.type === 'url') {
    const response = await fetch(source.data as string);
    if (!response.ok) {
      throw new Error(t('Failed to fetch image'));
    }
    const blob = await response.blob();
    const file = new File([blob], 'image.jpg', {
      type: blob.type || 'image/jpeg',
    });
    return file;
  }

  // Case 3: Base64 data - convert to File
  const base64 = source.data as string;
  const response = await fetch(base64);
  const blob = await response.blob();
  const file = new File([blob], 'image.jpg', {
    type: blob.type || 'image/jpeg',
  });
  return file;
};

const rotateImage = (
  img: HTMLImageElement,
  rotation: number,
): HTMLCanvasElement | null => {
  if (rotation === 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is not supported');
  }

  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));

  // Calculate new dimensions after rotation
  const newWidth = img.width * cos + img.height * sin;
  const newHeight = img.width * sin + img.height * cos;

  canvas.width = newWidth;
  canvas.height = newHeight;

  // Rotate around center
  ctx.translate(newWidth / 2, newHeight / 2);
  ctx.rotate(rad);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  return canvas;
};

const cropImage = (
  source: HTMLImageElement | HTMLCanvasElement,
  crop: CropArea,
  targetCanvas: HTMLCanvasElement,
): void => {
  const ctx = targetCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is not supported');
  }

  targetCanvas.width = Math.round(crop.width);
  targetCanvas.height = Math.round(crop.height);

  ctx.drawImage(
    source,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    targetCanvas.width,
    targetCanvas.height,
  );
};

const drawFullImage = (
  source: HTMLImageElement | HTMLCanvasElement,
  targetCanvas: HTMLCanvasElement,
): void => {
  const ctx = targetCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas is not supported');
  }

  targetCanvas.width = source.width;
  targetCanvas.height = source.height;
  ctx.drawImage(source, 0, 0);
};

const canvasToFile = (
  canvas: HTMLCanvasElement,
  originalFile: File,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to convert canvas to blob'));
          return;
        }

        const processedFile = new File(
          [blob],
          originalFile.name || 'processed.jpg',
          {
            type: blob.type || 'image/jpeg',
          },
        );

        resolve(processedFile);
      },
      originalFile.type || 'image/jpeg',
      0.95, // JPEG quality (95%)
    );
  });
};

const applyTransformations = async (
  img: HTMLImageElement,
  transform: ImageTransform,
  file: File,
): Promise<File> => {
  const rotation = transform.rotate || 0;
  const crop = transform.crop;

  // Apply rotation if needed
  const rotatedCanvas = rotateImage(img, rotation);
  const sourceImage = rotatedCanvas || img;

  // Create final canvas
  const finalCanvas = document.createElement('canvas');

  // Apply crop or draw full image
  if (crop) {
    cropImage(sourceImage, crop, finalCanvas);
  } else {
    drawFullImage(sourceImage, finalCanvas);
  }

  // Convert to file
  return canvasToFile(finalCanvas, file);
};

export const processImage = async (
  source: ImageSource,
  transform: ImageTransform,
  t: Translate,
): Promise<File> => {
  // Check cache first
  const cacheKey = getCacheKey(source, transform);
  const cached = PROCESSED_IMAGE_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Get File object from source
  const file = await getFileFromSource(source, t);

  // If no transformations needed, return original file
  if (!transform.crop && !transform.rotate && !transform.scale) {
    return file;
  }

  // Load image and apply transformations
  const img = new Image();
  const url = URL.createObjectURL(file);

  try {
    const processedFile = await new Promise<File>((resolve, reject) => {
      img.onload = async () => {
        try {
          const result = await applyTransformations(img, transform, file);
          URL.revokeObjectURL(url); // Clean up
          resolve(result);
        } catch (error) {
          URL.revokeObjectURL(url); // Clean up even on error
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(t('Failed to load image for processing')));
      };

      img.src = url;
    });

    // Cache the result
    PROCESSED_IMAGE_CACHE.set(cacheKey, processedFile);

    // Limit cache size to prevent memory leaks
    if (PROCESSED_IMAGE_CACHE.size > MAX_CACHE_SIZE) {
      // Remove oldest entry (first key in the Map)
      const firstKey = PROCESSED_IMAGE_CACHE.keys().next().value;
      if (firstKey) {
        PROCESSED_IMAGE_CACHE.delete(firstKey);
      }
    }

    return processedFile;
  } catch (error) {
    // Ensure cleanup happened and re-throw
    URL.revokeObjectURL(url);
    throw error;
  }
};
